import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useAddWarehouse, useUpdateWarehouse } from "../../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface WarehouseFormProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  editData?: any;
  onSuccess?: (newData: any) => void;
}

export function WarehouseForm({
  open,
  onOpenChange,
  editData,
  onSuccess,
}: WarehouseFormProps) {
  const { toast } = useToast();
  const isEditMode = Boolean(editData);
  const { mutate: addWarehouse } = useAddWarehouse();
  const { mutate: updateWarehouse } = useUpdateWarehouse();

  const getInitialValues = () => {
    if (!editData) {
      return {
        name: "",
        code: "",
        description: "",
        address: "",
        latitude: "",
        longitude: "",
      };
    }

    return {
      name: editData.name || "",
      code: editData.code || "",
      description: editData.description || "",
      address: editData.address || "",
      latitude: editData.latitude || "",
      longitude: editData.longitude || "",
    };
  };

  const formFields: FormField[] = [
    // Basic Information Group
    {
      name: "name",
      type: "text",
      label: "Warehouse Name",
      placeholder: "Enter warehouse name...",
      required: true,
      group: "Basic information",
      groupColorNeeded: false,
      groupHelperText: "Register a new warehouse location",
      className: "md:col-span-1",
    },
    {
      name: "code",
      type: "text",
      label: "Warehouse Code",
      placeholder: "Enter warehouse code...",
      required: true,
      group: "Basic information",
      className: "md:col-span-1",
      helperText: "Short unique identifier for this warehouse",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Brief description of this warehouse and its purpose…",
      group: "Basic information",
      className: "md:col-span-2",
    },

    // Location Details Group
    {
      name: "address",
      type: "text",
      label: "Address",
      placeholder: "e.g. 101 Industrial Ave, Central District, CA 90001",
      required: true,
      group: "Location Details",
      groupColorNeeded: false,
      className: "md:col-span-2",
      helperText: "Full street address of the warehouse",
    },
    {
      name: "latitude",
      type: "text",
      label: "Latitude",
      placeholder: "Latitude (e.g. 34.0522° N)",
      group: "Location Details",
      className: "md:col-span-1",
    },
    {
      name: "longitude",
      type: "text",
      label: "Longitude",
      placeholder: "Longitude (e.g. 118.2437° W)",
      group: "Location Details",
      className: "md:col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: getInitialValues(),
  });

  // Reset form when dialog opens/closes or editData changes
  useEffect(() => {
    if (open) {
      form.reset(getInitialValues());
    }
  }, [open, editData]);

  const handleSubmit = (formData: any) => {
    const payload = {
      name: formData.name,
      code: formData.code,
      description: formData.description || "",
      remote_utility_id: getRemoteUtilityId(),
      address: formData.address,
      latitude: formData.latitude || "",
      longitude: formData.longitude || "",
    };

    if (isEditMode) {
      updateWarehouse(
        { id: editData.id, payload },
        {
          onSuccess: (res) => {
            if (onSuccess) onSuccess(res?.result || { ...payload, id: editData.id });
            onOpenChange?.(false);
          },
        }
      );
    } else {
      addWarehouse(payload, {
        onSuccess: (res) => {
          if (onSuccess) onSuccess(res?.result || { ...payload, id: String(Date.now()) });
          onOpenChange?.(false);
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit warehouse" : "Add warehouse"}
          </DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => onOpenChange?.(false)}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: isEditMode ? "Update warehouse" : "Add warehouse",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
